import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { FiMessageCircle, FiX, FiSend, FiMinimize2, FiMaximize2 } from 'react-icons/fi';
import styles from './Chatbot.module.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  
  const [awaitingUserInfo, setAwaitingUserInfo] = useState(true);
  const [collectionStep, setCollectionStep] = useState('name');
  const [userInfo, setUserInfo] = useState({
    name: '',
    company: '',
    email: '',
    phone: ''
  });
  const [userId, setUserId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);

  useEffect(() => {
    initializeSession();
  }, []);

  const initializeSession = async () => {
    let existingSessionId = localStorage.getItem('chat_session_id');
    
    if (!existingSessionId) {
      existingSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('chat_session_id', existingSessionId);
    }
    
    setSessionId(existingSessionId);
    console.log('🆔 Session ID:', existingSessionId);
    
    try {
      const { data: existingUser, error } = await supabase
        .from('chat_users')
        .select('*')
        .eq('session_id', existingSessionId)
        .maybeSingle();
      
      if (error) {
        console.error('Error checking user:', error);
      }
      
      if (existingUser && existingUser.name) {
        setUserId(existingUser.id);
        setUserInfo({
          name: existingUser.name,
          company: existingUser.company_name || '',
          email: existingUser.email || '',
          phone: existingUser.phone || ''
        });
        setAwaitingUserInfo(false);
        
        setMessages([
          {
            id: 1,
            text: `👋 Welcome back, ${existingUser.name}! How can I help you today?`,
            sender: 'bot',
            timestamp: new Date()
          }
        ]);
      } else {
        setAwaitingUserInfo(true);
        setCollectionStep('name');
        setMessages([
          {
            id: 1,
            text: "👋 Hi! Welcome to Brandverse. May I know your name?",
            sender: 'bot',
            timestamp: new Date()
          }
        ]);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const saveUserToDatabase = async () => {
    try {
      const userData = {
        session_id: sessionId,
        name: userInfo.name,
        company_name: userInfo.company,
        email: userInfo.email,
        phone: userInfo.phone || null,
        last_active: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('chat_users')
        .upsert(userData, { onConflict: 'session_id' })
        .select();
      
      if (error) {
        console.error('Save user error:', error);
        return null;
      }
      
      if (data && data[0]) {
        setUserId(data[0].id);
        return data[0].id;
      }
      return null;
    } catch (err) {
      console.error('Error:', err);
      return null;
    }
  };

  const saveConversation = async (userMessage, botResponse) => {
    try {
      const conversationData = {
        session_id: sessionId,
        user_id: userId,
        user_message: userMessage,
        bot_response: botResponse,
        created_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('chatbot_conversations')
        .insert(conversationData);
      
      if (error) {
        console.error('Save conversation error:', error);
      } else {
        console.log('Conversation saved successfully');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const processUserInfo = async (message) => {
    const trimmedMessage = message.trim();
    let response = '';
    let nextStep = collectionStep;
    
    switch (collectionStep) {
      case 'name':
        if (trimmedMessage.length < 2) {
          response = "Could you please tell me your name?";
          nextStep = 'name';
        } else {
          setUserInfo(prev => ({ ...prev, name: trimmedMessage }));
          response = `Nice to meet you, ${trimmedMessage}! Which company do you work for?`;
          nextStep = 'company';
        }
        break;
        
      case 'company':
        setUserInfo(prev => ({ ...prev, company: trimmedMessage }));
        response = "Thanks! What's your email address?";
        nextStep = 'email';
        break;
        
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedMessage)) {
          response = "Please enter a valid email address.";
          nextStep = 'email';
        } else {
          setUserInfo(prev => ({ ...prev, email: trimmedMessage }));
          response = "One last thing - your phone number? (Type 'skip' to continue)";
          nextStep = 'phone';
        }
        break;
        
      case 'phone':
        if (trimmedMessage.toLowerCase() !== 'skip') {
          setUserInfo(prev => ({ ...prev, phone: trimmedMessage }));
        }
        await saveUserToDatabase();
        setAwaitingUserInfo(false);
        response = `Thanks ${userInfo.name}! 🎉 How can I help you today?\n\nYou can ask me about:\n• Web Development\n• Mobile Apps\n• AI Solutions\n• Enterprise Systems\n• Startup Program\n\nWhat would you like to know?`;
        nextStep = 'done';
        break;
        
      default:
        break;
    }
    
    setCollectionStep(nextStep);
    return response;
  };

  const getBotResponse = async (userMessage) => {
    const query = userMessage.toLowerCase();
    const name = userInfo.name || 'there';
    
    // ===== BYE / GOODBYE MESSAGE =====
    if (query.match(/bye|goodbye|see you|bye bye|take care|have a good day|talk to you later/)) {
      return `Goodbye ${name}! 👋 

Thank you for chatting with Brandverse Technologies.

**Remember, we're always here to help:**

📧 Email: contact@brandversetech.com
📱 Phone: +91 9154884214
💬 WhatsApp: +91 9154884214

Have a great day! Feel free to come back anytime you have questions about web development, mobile apps, AI solutions, or enterprise systems.

Take care! 😊`;
    }

    // ===== THANK YOU MESSAGE - PROVIDE CONTACT DETAILS =====
    if (query.match(/thank|thanks|appreciate|grateful/)) {
      return `You're very welcome, ${name}! 😊 

We'd love to help you further with your project.

**📞 Contact Our Team Directly:**

📧 **Email:** contact@brandversetech.com
📱 **Phone:** +91 9154884214
💬 **WhatsApp:** +91 9154884214
🌐 **Website:** brandversetech.com

📍 **Office:** Tadepalli, Vijayawada - 522502, Andhra Pradesh

**Our team is available:** Monday - Saturday, 9:00 AM - 7:00 PM

Feel free to reach out anytime - we're here to help! 🚀`;
    }

    // ===== CONTACT INFORMATION =====
    if (query.match(/contact|reach|email|phone|call|whatsapp|address|location|office|talk|speak to human|customer support|connect/)) {
      return `**Contact Brandverse Technologies, ${name}:** 📞

**Connect With Us:**

📧 **Email:** contact@brandversetech.com
📱 **Phone:** +91 9154884214
💬 **WhatsApp:** +91 9154884214

📍 **Address:**
Tadepalli, Vijayawada - 522502
Andhra Pradesh, India

🌐 **Website:** brandversetech.com

**Business Hours:**
Monday - Saturday: 9:00 AM - 7:00 PM
Sunday: Closed

**Response Time:** Within 24 hours

📞 **Need immediate help?** Call us now at +91 9154884214

We look forward to hearing from you! 😊`;
    }

    // ===== SERVICES OVERVIEW =====
    if (query.match(/what services do you offer|what do you do|services|offerings|what can you do|tell me about your services|what are your services/)) {
      return `Thanks for asking, ${name}! 🚀

**Brandverse Technologies offers 4 main services:**

🌐 **1. WEB DEVELOPMENT**
• Custom business websites
• E-commerce platforms
• Web applications
• Startup MVP development
• SEO optimization

📱 **2. MOBILE APP DEVELOPMENT**
• Native Android apps
• Native iOS apps
• Cross-platform apps
• App store submission

🤖 **3. AI & AUTOMATION**
• Smart AI chatbots
• Workflow automation
• Predictive analytics
• Document processing

🏢 **4. ENTERPRISE SOLUTIONS**
• Custom ERP systems
• CRM platforms
• HRMS solutions
• Inventory management

**Which service would you like to know more about?** 💡`;
    }

    // ===== WEB DEVELOPMENT DETAILS =====
    if (query.match(/web development|website|web dev|web design|ecommerce|e-commerce|online store|business website|web application/)) {
      return `**Web Development Services, ${name}:** 🌐

We build:
✅ Business websites that convert visitors to customers
✅ E-commerce platforms with secure payments
✅ Web applications with modern features
✅ Startup MVPs to test your idea quickly

**Features we provide:**
• Mobile-responsive design
• SEO optimization for Google ranking
• Fast loading speed
• Secure with SSL certificates
• Easy content management system

📞 **For pricing and timeline, please contact our team:**
📧 Email: contact@brandversetech.com
📱 Phone: +91 9154884214

**Ready to discuss your web project?** Reach out to us!`;
    }

    // ===== MOBILE APP DETAILS =====
    if (query.match(/mobile app|android app|ios app|iphone app|app development|mobile application|play store|app store/)) {
      return `**Mobile App Development Services, ${name}:** 📱

We build:
✅ Native Android apps
✅ Native iOS apps
✅ Cross-platform apps

**What we provide:**
• Beautiful UI/UX design
• Push notifications
• Payment gateway integration
• GPS/Location services
• App store submission & optimization

📞 **For pricing and timeline, please contact our team:**
📧 Email: contact@brandversetech.com
📱 Phone: +91 9154884214

**Have an app idea?** Let's discuss it with our team!`;
    }

    // ===== AI & AUTOMATION DETAILS =====
    if (query.match(/ai|artificial intelligence|chatbot|automation|machine learning|ml|intelligent|smart system/)) {
      return `**AI & Automation Services, ${name}:** 🤖

We provide:

✅ **AI CHATBOTS**
• 24/7 customer support
• Website & WhatsApp integration

✅ **WORKFLOW AUTOMATION**
• Automate repetitive tasks
• Save hundreds of hours

✅ **PREDICTIVE ANALYTICS**
• Sales forecasting
• Business insights

✅ **DOCUMENT PROCESSING**
• Automated data extraction
• Smart document handling

**Benefits:**
• Reduce operational costs by 40-60%
• Improve response time significantly

📞 **For pricing and timeline, please contact our team:**
📧 Email: contact@brandversetech.com
📱 Phone: +91 9154884214

**Ready to automate your business?** Contact us!`;
    }

    // ===== ENTERPRISE SOLUTIONS =====
    if (query.match(/enterprise|erp|crm|hrms|inventory|business system|management system/)) {
      return `**Enterprise Solutions, ${name}:** 🏢

We build custom business software:

✅ **ERP Systems** - Complete business management
✅ **CRM Platforms** - Sales & customer management
✅ **HRMS Solutions** - Payroll, attendance, recruitment
✅ **Inventory Management** - Stock tracking, orders

**Benefits:**
• Streamline your operations
• Reduce manual work by 60%
• Real-time data access
• Scalable as you grow

📞 **For pricing and timeline, please contact our team:**
📧 Email: contact@brandversetech.com
📱 Phone: +91 9154884214

**Transform your business operations** - Contact us today!`;
    }

    // ===== STARTUP PROGRAM =====
    if (query.match(/startup program|tech partner|mvp development|startup|mvp|minimum viable product/)) {
      return `**Startup Tech Partner Program, ${name}:** 🚀

We help startups succeed with:

✅ **MVP DEVELOPMENT**
• Test your idea with minimal budget
• Launch in 4-6 weeks
• Get user feedback quickly

✅ **TECHNICAL CONSULTING**
• Expert architecture guidance
• Right technology selection

✅ **PRODUCT SCALING**
• Scale to handle millions of users
• Performance optimization

**Benefits for Startups:**
• 50% faster time-to-market
• 40% lower development costs
• Expert technical guidance

📞 **For pricing and program details, contact our team:**
📧 Email: contact@brandversetech.com
📱 Phone: +91 9154884214

**Ready to launch your startup?** Let's discuss your idea!`;
    }

    // ===== ABOUT COMPANY =====
    if (query.match(/about company|about brandverse|who are you|background|history|founded|story|mission|vision|what is brandverse/)) {
      return `**About Brandverse Technologies, ${name}:** ✨

**Founded:** 2025

**Mission:** To help startups and businesses build impactful digital solutions that solve real-world challenges.

**Company Overview:**

Brandverse Technologies India Pvt. Ltd. is an innovation-driven technology company specializing in:
• Web Development
• Mobile App Development
• Enterprise Solutions
• AI & Automation

**Achievements:**
✅ 50+ successful projects delivered
✅ 97% client satisfaction rate
✅ 24/7 technical support

**Why Choose Brandverse?**
✨ Innovation-driven approach
✨ Quality assured delivery
✨ On-time project completion
✨ Dedicated support team

📞 **Ready to work with us?** Contact our team to discuss your project!`;
    }

    // ===== INDUSTRIES WE SERVE =====
    if (query.match(/industries|sectors|domains|who do you work with|clients type|business type|what industries/)) {
      return `**Industries We Serve, ${name}:** 🏭

**We work with:**

🚀 **STARTUPS & ENTREPRENEURS**
• MVP development
• Product scaling
• Tech consulting

🎓 **EDUCATION & EDTECH**
• Learning management systems
• Student portals

🏠 **REAL ESTATE & PROPERTY**
• Property listing portals
• CRM for real estate

💼 **SMALL & MEDIUM BUSINESSES**
• Digital transformation
• Business automation

🏥 **HEALTHCARE**
• Patient management systems
• Telemedicine platforms

🛍️ **RETAIL & E-COMMERCE**
• Online stores
• Inventory management

**No matter your industry - we can help!**

📞 **Contact us:** contact@brandversetech.com | +91 9154884214`;
    }

    // ===== PRICING & TIMELINE =====
    if (query.match(/price|cost|how much|quote|budget|expensive|affordable|rates|fee|charges|pricing|timeline|duration|how long|when|delivery|deadline|complete|finish|take|timeframe/)) {
      return `**${name}, thank you for your interest!** 💰

For **pricing** and **project timeline** details, our team will provide you with a customized quote based on your specific requirements.

**Please contact our sales team:**

📧 **Email:** contact@brandversetech.com
📱 **Phone:** +91 9154884214
💬 **WhatsApp:** +91 9154884214

**Why contact us directly?**
✅ Every project is unique
✅ We'll understand your requirements
✅ Get an accurate, customized quote
✅ Receive a detailed timeline

**Response time:** Within 24 hours

📞 **We're looking forward to helping you with your project!**`;
    }

    // ===== GREETINGS =====
    if (query.match(/^(hello|hi|hey|greetings|good morning|good afternoon|namaste)$/)) {
      return `Hello ${name}! 👋 Welcome to Brandverse Technologies.

I'm here to help you with information about our services. 

💡 **You can ask me about:**
• Our Services (Web, Mobile, AI, Enterprise)
• Project Details
• Contact Information

**How can I help you today?** 😊`;
    }

    // ===== DEFAULT =====
    return `Thanks for reaching out, ${name}! 💡

I want to make sure I help you properly. 

**Here's what you can ask me:**

📌 "What services do you offer?"
📌 "Tell me about web development"
📌 "Tell me about mobile app development"
📌 "Tell me about AI solutions"
📌 "Tell me about enterprise solutions"
📌 "Contact your sales team"

**For pricing and timeline, please contact our team directly:**

📧 Email: contact@brandversetech.com
📱 Phone: +91 9154884214

**What would you like to know about Brandverse?** 😊`;
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessageText = inputMessage;
    const userMessage = {
      id: Date.now(),
      text: userMessageText,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    let botResponseText;

    try {
      if (awaitingUserInfo) {
        botResponseText = await processUserInfo(userMessageText);
      } else {
        botResponseText = getBotResponse(userMessageText);
        await saveConversation(userMessageText, botResponseText);
      }
    } catch (error) {
      console.error('Error:', error);
      botResponseText = "I'm having trouble right now. Please contact us at contact@brandversetech.com for immediate assistance.";
    }
    
    const botMessage = {
      id: Date.now() + 1,
      text: botResponseText,
      sender: 'bot',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, botMessage]);
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  const skipPhone = () => {
    if (collectionStep === 'phone') {
      processUserInfo('skip');
    }
  };

  return (
    <>
      {!isOpen && (
        <button className={styles.chatButton} onClick={() => setIsOpen(true)}>
          <FiMessageCircle />
          <span className={styles.chatBadge}>AI</span>
        </button>
      )}

      {isOpen && (
        <div className={`${styles.chatWindow} ${isMinimized ? styles.minimized : ''}`}>
          <div className={styles.chatHeader}>
            <div className={styles.chatHeaderInfo}>
              <div className={styles.chatAvatar}>🤖</div>
              <div>
                <h3>Brandverse Assistant</h3>
                <p>{awaitingUserInfo ? 'Getting to know you' : 'Online'}</p>
              </div>
            </div>
            <div className={styles.chatHeaderActions}>
              <button onClick={() => setIsMinimized(!isMinimized)} className={styles.headerButton}>
                {isMinimized ? <FiMaximize2 /> : <FiMinimize2 />}
              </button>
              <button onClick={() => setIsOpen(false)} className={styles.headerButton}>
                <FiX />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              <div className={styles.chatMessages}>
                {messages.map((message) => (
                  <div key={message.id} className={`${styles.message} ${styles[message.sender]}`}>
                    <div className={styles.messageContent}>
                      <div className={styles.messageText}>{message.text}</div>
                      <div className={styles.messageTime}>
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className={`${styles.message} ${styles.bot}`}>
                    <div className={styles.typingIndicator}>
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className={styles.chatInput}>
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  rows={1}
                />
                {collectionStep === 'phone' && awaitingUserInfo && (
                  <button onClick={skipPhone} className={styles.skipButton}>
                    Skip
                  </button>
                )}
                <button onClick={handleSendMessage} disabled={isLoading}>
                  <FiSend />
                </button>
              </div>

              {!awaitingUserInfo && (
                <div className={styles.chatFooter}>
                  <button onClick={scrollToContact} className={styles.footerButton}>
                    Talk to Sales →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;